/*
  Brand video player
  ------------------
  The controller behind <brand-video-player>. Same behaviour as the custom player
  in the home Video Story section, minus the teaser loop and the story panel:
  a poster that starts the film, a centre play / replay button, a close button
  that returns to the poster, and a control bar with play, time, seek, mute and
  full screen. While playing, the chrome hides 1s after the pointer goes still,
  or at once when it leaves the player, and comes back on move, tap or focus.
  The video carries no src until the visitor asks for it, so nothing is
  downloaded on page load.

  Markup: snippets/brand-video-player.liquid
  Styles: assets/component-brand-video-player.css
*/

(function () {
  // Pointer resting on the player, and pointer gone from it. Leaving is an
  // unambiguous "done with the controls", so it hides with no wait at all.
  var UI_HIDE_DELAY = 1000;
  var UI_LEAVE_DELAY = 0;

  function fmt(seconds) {
    if (!isFinite(seconds) || seconds < 0) seconds = 0;
    var total = Math.floor(seconds);
    var m = Math.floor(total / 60);
    var s = total % 60;
    return m + ':' + (s < 10 ? '0' : '') + s;
  }

  class BrandVideoPlayer extends HTMLElement {
    connectedCallback() {
      if (this._init) {
        this._bindDocument();
        return;
      }
      this._init = true;

      this.video = this.querySelector('[data-bvp-video]');
      if (!this.video) return;

      this.poster = this.querySelector('[data-bvp-open]');
      this.player = this.querySelector('[data-bvp-player]');
      this.bigBtn = this.querySelector('[data-bvp-bigplay]');
      this.exitBtn = this.querySelector('[data-bvp-exit]');
      this.toggleBtn = this.querySelector('[data-bvp-toggle]');
      this.muteBtn = this.querySelector('[data-bvp-mute]');
      this.fsBtn = this.querySelector('[data-bvp-fs]');
      this.seek = this.querySelector('[data-bvp-seek]');
      this.curEl = this.querySelector('[data-bvp-cur]');
      this.durEl = this.querySelector('[data-bvp-dur]');

      this.active = false;
      this.scrubbing = false;
      this.uiTimer = null;
      this.loaded = false;
      // Only a keyboard visitor needs the chrome to stay put while a control
      // holds focus. A mouse visitor gets it out of the way, see _showUI.
      this.keyboardNav = false;

      this._onFsChange = this._onFsChange.bind(this);

      this._bind();
      this._bindDocument();
    }

    disconnectedCallback() {
      clearTimeout(this.uiTimer);
      document.removeEventListener('fullscreenchange', this._onFsChange);
      document.removeEventListener('webkitfullscreenchange', this._onFsChange);
    }

    _bindDocument() {
      document.addEventListener('fullscreenchange', this._onFsChange);
      document.addEventListener('webkitfullscreenchange', this._onFsChange);
    }

    /* ---------- wiring ---------- */

    _bind() {
      var self = this;

      if (this.poster) {
        this.poster.addEventListener('click', function (e) {
          // Enter or Space on a button fires a click with detail 0. A real
          // pointer click reports 1 or more, so this tells the two apart.
          self.keyboardNav = e.detail === 0;
          self.activate();
        });
      }

      this.addEventListener('click', function (e) {
        if (!self.active) return;
        if (e.target.closest('button') || e.target.closest('.bvp__bar')) return;
        // On touch the first tap only brings the controls back.
        if (self.classList.contains('is-ui-hidden')) {
          self._showUI();
          return;
        }
        self._toggle();
      });

      this.addEventListener('pointermove', function () {
        self.keyboardNav = false;
        if (self.active) self._showUI();
      });

      this.addEventListener('pointerdown', function () {
        self.keyboardNav = false;
      });

      // The pointer leaving the player is a clear "done with the controls", so
      // the chrome goes sooner than it does while the pointer rests on it.
      // keyboardNav is left alone here, so a control holding keyboard focus
      // still keeps the chrome up even if the mouse drifts away.
      this.addEventListener('pointerleave', function () {
        if (self.active) self._scheduleHide(UI_LEAVE_DELAY);
      });

      this.addEventListener('keydown', function () {
        self.keyboardNav = true;
      });

      this.addEventListener('keydown', this._onKey.bind(this));
      this.addEventListener('focusin', function () {
        if (self.active) self._showUI();
      });

      if (this.bigBtn) {
        this.bigBtn.addEventListener('click', function () {
          if (self.classList.contains('is-ended')) self.video.currentTime = 0;
          self._toggle();
        });
      }

      if (this.toggleBtn) {
        this.toggleBtn.addEventListener('click', function () {
          self._toggle();
        });
      }

      if (this.exitBtn) {
        this.exitBtn.addEventListener('click', function () {
          self.deactivate();
        });
      }

      if (this.muteBtn) {
        this.muteBtn.addEventListener('click', function () {
          self.video.muted = !self.video.muted;
          if (!self.video.muted && self.video.volume === 0) self.video.volume = 1;
        });
      }

      if (this.fsBtn) {
        this.fsBtn.addEventListener('click', function () {
          self._fullscreen();
        });
      }

      if (this.seek) {
        this.seek.addEventListener('input', function () {
          self.scrubbing = true;
          var d = self.video.duration;
          if (!isFinite(d) || d <= 0) return;
          var pct = self.seek.value / 10;
          self.seek.style.setProperty('--p', pct.toFixed(2));
          if (self.curEl) self.curEl.textContent = fmt((d * pct) / 100);
        });

        this.seek.addEventListener('change', function () {
          var d = self.video.duration;
          if (isFinite(d) && d > 0) {
            self.video.currentTime = (d * (self.seek.value / 10)) / 100;
          }
          self.scrubbing = false;
          self._showUI();
        });
      }

      this.video.addEventListener('loadedmetadata', function () {
        if (self.durEl) self.durEl.textContent = fmt(self.video.duration);
      });
      this.video.addEventListener('play', function () {
        self.classList.remove('is-paused', 'is-ended');
        if (self.toggleBtn) self.toggleBtn.setAttribute('aria-label', 'Pause');
        if (self.bigBtn) self.bigBtn.setAttribute('aria-label', 'Pause');
        self._showUI();
      });
      this.video.addEventListener('pause', function () {
        self.classList.add('is-paused');
        if (self.toggleBtn) self.toggleBtn.setAttribute('aria-label', 'Play');
        if (self.bigBtn) self.bigBtn.setAttribute('aria-label', 'Play');
        self._showUI();
      });
      this.video.addEventListener('ended', function () {
        self.classList.add('is-ended', 'is-paused');
        if (self.bigBtn) self.bigBtn.setAttribute('aria-label', 'Replay');
        self._showUI();
      });
      this.video.addEventListener('timeupdate', function () {
        self._syncTime();
      });
      this.video.addEventListener('progress', function () {
        self._syncBuffer();
      });
      this.video.addEventListener('waiting', function () {
        self.classList.add('is-buffering');
      });
      this.video.addEventListener('playing', function () {
        self.classList.remove('is-buffering');
      });
      this.video.addEventListener('canplay', function () {
        self.classList.remove('is-buffering');
      });
      this.video.addEventListener('volumechange', function () {
        self._syncMute();
      });
    }

    /* ---------- open and close ---------- */

    activate() {
      if (this.active) return;
      this.active = true;
      this.classList.add('is-active');

      // Nothing else on the page should compete for sound. The theme helper
      // pauses every video and app player, including ours, so it runs first.
      if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();

      this._load();
      this.video.muted = false;
      this.classList.add('is-buffering');
      this._play();
      this._showUI();
      // Taking focus into the bar would pin the chrome open for a mouse
      // visitor, because _showUI leaves it alone while a control is focused.
      // Only a keyboard visitor needs to land on the play button.
      if (this.keyboardNav && this.toggleBtn) this.toggleBtn.focus({ preventScroll: true });
    }

    deactivate() {
      if (!this.active) return;
      this.active = false;
      this.classList.remove('is-active', 'is-ended', 'is-ui-hidden', 'is-buffering');
      clearTimeout(this.uiTimer);
      try {
        this.video.pause();
        this.video.currentTime = 0;
        this.video.muted = false;
      } catch (e) {
        /* the file may not have loaded yet */
      }
      this.classList.remove('is-muted', 'is-paused');
      if (this.poster) this.poster.focus({ preventScroll: true });
    }

    _load() {
      if (this.loaded) return;
      this.loaded = true;
      var sources = this.video.querySelectorAll('source[data-src]');
      if (sources.length) {
        sources.forEach(function (s) {
          s.setAttribute('src', s.getAttribute('data-src'));
          s.removeAttribute('data-src');
        });
      } else if (this.video.getAttribute('data-src')) {
        this.video.src = this.video.getAttribute('data-src');
      }
      this.video.load();
    }

    _play() {
      var self = this;
      var p = this.video.play();
      if (p && typeof p.catch === 'function') {
        p.catch(function () {
          // Sound was refused, which is rare right after a click. Start muted
          // rather than not starting at all, and let the visitor unmute. If the
          // player was closed while that promise was pending, stay closed.
          if (!self.active) return;
          self.video.muted = true;
          var q = self.video.play();
          if (q && typeof q.catch === 'function') {
            q.catch(function () {
              self.classList.add('is-paused');
              self.classList.remove('is-buffering');
            });
          }
        });
      }
    }

    _toggle() {
      if (this.video.paused) this._play();
      else this.video.pause();
    }

    _nudge(seconds) {
      var d = this.video.duration;
      if (!isFinite(d) || d <= 0) return;
      this.video.currentTime = Math.min(Math.max(this.video.currentTime + seconds, 0), d);
    }

    _volume(delta) {
      var v = Math.min(Math.max(this.video.volume + delta, 0), 1);
      this.video.volume = v;
      if (v > 0) this.video.muted = false;
    }

    /* ---------- UI sync ---------- */

    _syncTime() {
      var d = this.video.duration;
      if (!isFinite(d) || d <= 0) return;
      var pct = (this.video.currentTime / d) * 100;
      if (!this.scrubbing && this.seek) {
        this.seek.value = String(Math.round(pct * 10));
        this.seek.style.setProperty('--p', pct.toFixed(2));
        this.seek.setAttribute('aria-valuetext', fmt(this.video.currentTime) + ' of ' + fmt(d));
      }
      if (this.curEl && !this.scrubbing) this.curEl.textContent = fmt(this.video.currentTime);
      if (this.durEl) this.durEl.textContent = fmt(d);
    }

    _syncBuffer() {
      if (!this.seek) return;
      var d = this.video.duration;
      var ranges = this.video.buffered;
      if (!isFinite(d) || d <= 0 || !ranges || !ranges.length) return;
      var t = this.video.currentTime;
      var end = 0;
      for (var i = 0; i < ranges.length; i++) {
        if (ranges.start(i) <= t && t <= ranges.end(i)) {
          end = ranges.end(i);
          break;
        }
        if (ranges.end(i) > end) end = ranges.end(i);
      }
      this.seek.style.setProperty('--b', ((end / d) * 100).toFixed(2));
    }

    _syncMute() {
      var muted = this.video.muted || this.video.volume === 0;
      this.classList.toggle('is-muted', muted);
      if (this.muteBtn) {
        this.muteBtn.setAttribute('aria-label', muted ? 'Unmute' : 'Mute');
        this.muteBtn.setAttribute('aria-pressed', muted ? 'true' : 'false');
      }
    }

    _showUI() {
      this.classList.remove('is-ui-hidden');
      this._scheduleHide(UI_HIDE_DELAY);
    }

    _scheduleHide(delay) {
      var self = this;
      clearTimeout(this.uiTimer);
      if (!this.active || this.video.paused) return;
      // A zero delay hides in the same tick, so the chrome is already gone by
      // the time the pointer has cleared the edge.
      if (delay <= 0) {
        this._hideUI();
        return;
      }
      this.uiTimer = setTimeout(function () {
        self._hideUI();
      }, delay);
    }

    _hideUI() {
      if (!this.active || this.video.paused || this.scrubbing) return;
      // A focused control only holds the chrome open for keyboard use. With a
      // mouse the bar goes once the pointer has been still for the delay.
      if (this.keyboardNav && this.player && this.player.contains(document.activeElement)) return;
      this.classList.add('is-ui-hidden');
    }

    /* ---------- full screen ---------- */

    _fullscreen() {
      var doc = document;
      if (doc.fullscreenElement || doc.webkitFullscreenElement) {
        if (doc.exitFullscreen) doc.exitFullscreen();
        else if (doc.webkitExitFullscreen) doc.webkitExitFullscreen();
        return;
      }
      if (this.requestFullscreen) {
        this.requestFullscreen().catch(function () {});
      } else if (this.webkitRequestFullscreen) {
        this.webkitRequestFullscreen();
      } else if (this.video.webkitEnterFullscreen) {
        // iPhone Safari: only the video element itself can go full screen.
        this.video.webkitEnterFullscreen();
      }
    }

    _onFsChange() {
      var el = document.fullscreenElement || document.webkitFullscreenElement;
      var on = el === this;
      this.classList.toggle('is-fs', on);
      if (this.fsBtn) {
        this.fsBtn.setAttribute('aria-label', on ? 'Exit full screen' : 'Full screen');
      }
    }

    /* ---------- keyboard ---------- */

    _onKey(e) {
      if (!this.active) return;
      var target = e.target;
      var onRange = target && target.classList && target.classList.contains('bvp__seek');
      var onButton = target && target.tagName === 'BUTTON';

      switch (e.key) {
        case ' ':
        case 'k':
        case 'K':
          if (onButton) return;
          e.preventDefault();
          this._toggle();
          break;
        case 'ArrowRight':
          if (onRange) return;
          e.preventDefault();
          this._nudge(5);
          break;
        case 'ArrowLeft':
          if (onRange) return;
          e.preventDefault();
          this._nudge(-5);
          break;
        case 'ArrowUp':
          if (onRange) return;
          e.preventDefault();
          this._volume(0.1);
          break;
        case 'ArrowDown':
          if (onRange) return;
          e.preventDefault();
          this._volume(-0.1);
          break;
        case 'm':
        case 'M':
          this.video.muted = !this.video.muted;
          break;
        case 'f':
        case 'F':
          this._fullscreen();
          break;
        case 'Escape':
          if (document.fullscreenElement || document.webkitFullscreenElement) return;
          e.preventDefault();
          this.deactivate();
          return;
        default:
          return;
      }
      this._showUI();
    }
  }

  if (!customElements.get('brand-video-player')) {
    customElements.define('brand-video-player', BrandVideoPlayer);
  }
})();
